import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { LifeBuoy, X, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supportAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * Widget flotante de soporte (bottom-right) visible para usuarios autenticados.
 * Crea un ticket persistente vía POST /api/support/ticket y notifica al admin.
 */
export default function SoporteForm() {
    const { user } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [asunto, setAsunto] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    // Permite abrir el form desde cualquier parte de la app:
    //   window.dispatchEvent(new CustomEvent('open-soporte', { detail: { asunto, mensaje } }))
    React.useEffect(() => {
        const handler = (e) => {
            const detail = e?.detail || {};
            if (detail.asunto) setAsunto(detail.asunto);
            if (detail.mensaje) setMensaje(detail.mensaje);
            setSent(false);
            setOpen(true);
        };
        window.addEventListener('open-soporte', handler);
        return () => window.removeEventListener('open-soporte', handler);
    }, []);

    // El FAB queda montado aunque user esté momentáneamente null (flicker de AuthContext)
    // para evitar race conditions justo post-login. El guard real se aplica en submit.

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error('Inicia sesión para enviar una consulta.');
            return;
        }
        if (mensaje.trim().length < 5) {
            toast.error('Describe tu consulta con un poco más de detalle.');
            return;
        }
        setSending(true);
        try {
            await supportAPI.createTicket({
                asunto: asunto.trim() || null,
                mensaje: mensaje.trim(),
                url: window.location.href,
                contexto_extra: {
                    path: location.pathname,
                    nombre: user?.nombre,
                },
            });
            setSent(true);
            setMensaje('');
            setAsunto('');
            setTimeout(() => {
                setOpen(false);
                setSent(false);
            }, 2400);
        } catch (err) {
            const detail = err?.response?.data?.detail || 'No pudimos enviar tu mensaje.';
            toast.error(detail);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {/* Botón flotante */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="soporte-fab-btn"
                    className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-lg shadow-slate-900/20 transition-all hover:bg-slate-800 hover:shadow-xl active:scale-95"
                    aria-label="Abrir formulario de soporte"
                >
                    <LifeBuoy className="h-4 w-4" />
                    <span className="hidden sm:inline">Soporte</span>
                </button>
            )}

            {/* Panel del formulario */}
            {open && (
                <div
                    className="fixed bottom-5 right-5 z-50 w-[min(380px,calc(100vw-2.5rem))] rounded-2xl border border-slate-200 bg-white shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200"
                    data-testid="soporte-form-panel"
                >
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-50">
                                <LifeBuoy className="h-4 w-4 text-amber-700" />
                            </div>
                            <div>
                                <p className="font-heading text-sm font-bold text-slate-900">¿Necesitas ayuda?</p>
                                <p className="text-xs text-slate-500">Te respondemos por correo en menos de 24 h.</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="soporte-close-btn"
                            className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                            aria-label="Cerrar"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Body */}
                    {sent ? (
                        <div className="flex flex-col items-center gap-3 px-5 py-10 text-center" data-testid="soporte-success">
                            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                            <p className="font-heading text-sm font-semibold text-slate-900">¡Mensaje enviado!</p>
                            <p className="text-xs text-slate-500">
                                Hemos registrado tu consulta. Nuestro equipo responderá a {user?.email || 'tu correo'} a la brevedad.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-3 px-5 py-4">
                            <div>
                                <Label htmlFor="soporte-asunto" className="text-xs text-slate-600">
                                    Asunto <span className="text-slate-400">(opcional)</span>
                                </Label>
                                <Input
                                    id="soporte-asunto"
                                    value={asunto}
                                    onChange={(e) => setAsunto(e.target.value)}
                                    placeholder="Ej: No puedo activar el plan"
                                    maxLength={160}
                                    data-testid="soporte-asunto-input"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label htmlFor="soporte-mensaje" className="text-xs text-slate-600">
                                    Cuéntanos qué necesitas <span className="text-rose-500">*</span>
                                </Label>
                                <Textarea
                                    id="soporte-mensaje"
                                    value={mensaje}
                                    onChange={(e) => setMensaje(e.target.value)}
                                    placeholder="Describe tu consulta o el problema que tienes…"
                                    rows={4}
                                    maxLength={5000}
                                    required
                                    data-testid="soporte-mensaje-input"
                                    className="mt-1 resize-none"
                                />
                                <p className="mt-1 text-[10px] text-slate-400">
                                    {mensaje.length}/5000{user?.email && <> — Te contactaremos a <strong>{user.email}</strong></>}
                                </p>
                            </div>
                            <Button
                                type="submit"
                                disabled={sending || mensaje.trim().length < 5}
                                className="w-full gap-2"
                                data-testid="soporte-submit-btn"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Enviando…
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4" />
                                        Enviar consulta
                                    </>
                                )}
                            </Button>
                        </form>
                    )}
                </div>
            )}
        </>
    );
}
